/**
 * IntegrationHealthBanner
 *
 * Banner genérico multi-provider que alerta o usuário quando uma integração
 * externa do tenant está degradada ou falhando. Reutilizável por qualquer
 * módulo (financial, messaging, calendar, signature, ...).
 *
 * Para o caso especial Asaas/webhook (módulo financeiro), oferece botão
 * "Registrar webhook agora" que dispara a action register_webhook da edge
 * function financial-provider.
 *
 * RELEASE: 2026-06-17 v1.0.0 - Versão inicial.
 */
import { useMemo, useState } from 'react';
import { useIntegrationHealth, type IntegrationHealthRow } from '@/hooks/integrations/useIntegrationHealth';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { createLogger } from '@/lib/logger';

const logger = createLogger('IntegrationHealthBanner');

interface IntegrationHealthBannerProps {
  module: string;
  className?: string;
}

// Rótulo amigável por provider para exibir na mensagem
const PROVIDER_LABELS: Record<string, string> = {
  asaas: 'Asaas',
  meta_whatsapp: 'WhatsApp Cloud',
  google_calendar: 'Google Calendar',
  'z-api': 'Z-API',
  clicksign: 'Clicksign',
  autentique: 'Autentique',
};

const RESOURCE_LABELS: Record<string, string> = {
  webhook: 'webhook',
  oauth_token: 'token de autenticação',
  api_key: 'chave de API',
  instance: 'instância',
};

export function IntegrationHealthBanner({ module, className }: IntegrationHealthBannerProps) {
  const { data, isLoading } = useIntegrationHealth(module);
  const queryClient = useQueryClient();
  const [repairing, setRepairing] = useState<string | null>(null);

  // Mostrar apenas integrações em estado problemático
  const problematic = useMemo(
    () => (data || []).filter(r => r.status === 'failed' || r.status === 'degraded'),
    [data],
  );

  if (isLoading || problematic.length === 0) return null;

  return (
    <div className={className}>
      {problematic.map((row) => (
        <BannerRow key={row.id} row={row} repairing={repairing === row.id}
          onRepair={async () => {
            // Caso especial: Asaas webhook → re-registrar via financial-provider
            if (row.module === 'financial' && row.provider_slug === 'asaas' && row.resource_kind === 'webhook') {
              setRepairing(row.id);
              try {
                const contaCorrenteId = (row.metadata as Record<string, unknown>)?.conta_corrente_id as string | undefined;
                if (!contaCorrenteId) {
                  toast.error('Não foi possível localizar a conta corrente para reparo');
                  return;
                }
                const { data: resp, error } = await supabase.functions.invoke('financial-provider', {
                  body: {
                    action: 'register_webhook',
                    provider_slug: 'asaas',
                    conta_corrente_id: contaCorrenteId,
                  },
                });
                if (error || !resp?.success) {
                  toast.error(resp?.error || 'Falha ao registrar webhook');
                } else {
                  toast.success('Webhook registrado com sucesso!');
                  queryClient.invalidateQueries({ queryKey: ['integration-health'] });
                }
              } catch (err) {
                logger.error('Falha no reparo manual', { error: err });
                toast.error('Erro inesperado ao registrar webhook');
              } finally {
                setRepairing(null);
              }
            }
          }}
        />
      ))}
    </div>
  );
}

function BannerRow({
  row,
  repairing,
  onRepair,
}: {
  row: IntegrationHealthRow;
  repairing: boolean;
  onRepair: () => void;
}) {
  const provider = PROVIDER_LABELS[row.provider_slug] || row.provider_slug;
  const resource = RESOURCE_LABELS[row.resource_kind] || row.resource_kind;

  const canRepair =
    row.module === 'financial' && row.provider_slug === 'asaas' && row.resource_kind === 'webhook';

  const message =
    row.status === 'failed'
      ? `O ${resource} do ${provider} não está ativo. Pagamentos podem não ser atualizados automaticamente.`
      : `O ${resource} do ${provider} está com problemas (${row.last_error || 'sem detalhes'}).`;

  return (
    <InlineNotice
      variant={row.status === 'failed' ? 'error' : 'warning'}
      className="mb-2 flex items-start justify-between gap-3"
    >
      <span className="flex-1">{message}</span>
      {canRepair && (
        <Button
          size="xs"
          variant="outline"
          onClick={onRepair}
          disabled={repairing}
          className="gap-1.5 shrink-0"
        >
          {repairing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Registrar webhook agora
        </Button>
      )}
    </InlineNotice>
  );
}
